import { Base64 } from 'js-base64'
import { useCallback, useContext, useEffect } from 'react'
import useFetch, { CachePolicies, Req } from 'use-http'
import type {
  ViewableParentRequest,
  ViewableParentSuccessResponse,
} from '../interfaces'
import { UserContext } from '../providers'
import { FeaturesEnum } from '../../utils/features'
import { addLeadingSlash, addTrailingSlash } from '../../utils/urlHelpers'
import { PublicLinksInfoContext } from '../providers/PublicLinksInfoProvider'

export const useSetFolderPermission = ({
  folderPath,
  onData,
  onLoading,
}: {
  folderPath: string
  onData(data: string): void
  onLoading(value: boolean): void
}): { loading: boolean; refetch(): Promise<void> } => {
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const { request, loading, data, response } = useFetch<{
    permission?: string
  }>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getFolderPermission = async (): Promise<void> => {
    if (
      folderPath !== '/' &&
      user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    ) {
      const endpoint = `/api/clients/${
        user?.clientNumber
      }/access-control/path/${Base64.encode(folderPath)}`
      await request.get(endpoint)
    } else {
      onData('')
    }
  }
  useEffect(() => {
    if (!isPublicLink) {
      void getFolderPermission()
    }
  }, [folderPath, user, isPublicLink])

  useEffect(() => {
    onLoading(loading)
  }, [loading])

  useEffect(() => {
    if (request.error) {
      if (response.status === 403) {
        onData('NOT_ALLOWED')
      } else if (response.status === 404) {
        onData('NOT_FOUND')
      } else {
        onData('')
      }
    } else {
      onData(data?.permission ?? '')
    }
  }, [data, request.error])

  const refetch = useCallback(() => getFolderPermission(), [folderPath, user])
  return { refetch, loading }
}

export const useSetViewableParent = ({
  folderPath,
  mediaCollectionId,
  onData,
  onLoading,
}: {
  folderPath: string
  mediaCollectionId: string | undefined
  onData(data: ViewableParentSuccessResponse | string): void
  onLoading(value: boolean): void
}): { loading: boolean; refetch(): Promise<void> } => {
  const { isPublicLink, urlPrefix, clientInfo } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const clientNumber = user?.clientNumber ?? clientInfo?.clientNumber
  const { request, loading, data, response } =
    useFetch<ViewableParentSuccessResponse>({
      cachePolicy: CachePolicies.NO_CACHE,
    })

  const getViewableParent = async (): Promise<void> => {
    if (
      isPublicLink ||
      user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    ) {
      if (folderPath !== '/') {
        const requestBody: ViewableParentRequest = {
          assetPath: folderPath,
        }
        if (mediaCollectionId) {
          requestBody.mediaCollectionId = mediaCollectionId
        }
        await request.post(
          `${urlPrefix}api/clients/${clientNumber}/access-control/viewable-parent`,
          requestBody,
        )
        return
      } else if (mediaCollectionId) {
        const requestBody: ViewableParentRequest = {
          mediaCollectionId,
        }

        await request.post(
          `${urlPrefix}api/clients/${clientNumber}/access-control/viewable-parent`,
          requestBody,
        )
        return
      }
    }
    onData({
      asset: {
        assetId: '',
        name: '',
        assetPath: '/',
      },
    })
  }

  useEffect(() => {
    void getViewableParent()
  }, [folderPath, mediaCollectionId, user, urlPrefix, clientInfo])

  useEffect(() => {
    onLoading(loading)
  }, [loading])

  useEffect(() => {
    if (request.error) {
      if (response.status === 403) {
        onData('NOT_ALLOWED')
      } else if (response.status === 404) {
        onData('NOT_FOUND')
      } else {
        onData('INTERNAL_ERROR')
      }
    } else {
      onData(
        data ?? {
          asset: {
            assetId: '',
            name: '',
            assetPath: '/',
          },
        },
      )
    }
  }, [data, request.error])

  const refetch = useCallback(
    () => getViewableParent(),
    [folderPath, mediaCollectionId, user, urlPrefix, clientInfo],
  )
  return { refetch, loading }
}
